import React, { useState, useEffect } from 'react';
import { Link, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';
import TextList from './TextList';
import TextForm from './TextForm';
import TextSubstitution from './TextSubstitution';
import EditTextForm from './EditTextForm';
import UserManagement from './UserManagement';
import Dashboard from './Dashboard';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

const NavLink = ({ to, children, icon }) => {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`${
        isActive ? 'bg-blue-600 text-white font-medium shadow-md' : 'text-gray-300 hover:bg-blue-500/60 hover:text-white'
      } flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all duration-200 ease-in-out mb-2`}
    >
      {icon && <span className="text-lg">{icon}</span>}
      {children}
    </Link>
  );
};

const AppContent = ({ setIsAuthenticated }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [userRole, setUserRole] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    setIsAuthenticated(false);
    navigate('/login');
  };

  // Gradiente para o tema escuro
  const darkGradient = 'bg-gradient-to-b from-gray-800 to-gray-900';
  // Gradiente para o tema claro
  const lightGradient = 'bg-gradient-to-b from-blue-600 to-blue-800';

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Sidebar */}
      <aside className={`${darkMode ? darkGradient : lightGradient} text-white transition-all duration-300 ease-in-out ${isSidebarOpen ? 'w-64' : 'w-20'} shadow-xl`}>
        <div className="p-4 flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between mb-8">
            {isSidebarOpen ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-xl font-bold flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Padronizador
              </motion.div>
            ) : (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="text-xl font-bold"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </motion.span>
            )}
            <button 
              onClick={toggleSidebar} 
              className="text-white p-1 rounded-lg hover:bg-white/20 focus:outline-none transition-colors duration-200"
            >
              {isSidebarOpen ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                </svg>
              )}
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex-grow mt-2">
            <NavLink to="/" icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }>
              {isSidebarOpen && "Lista de Textos"}
            </NavLink>
            
            <NavLink to="/substituir" icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }>
              {isSidebarOpen && "Substituir Variáveis"}
            </NavLink>
            
            {userRole === 'admin' && (
              <>
                <NavLink to="/cadastrar" icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                }>
                  {isSidebarOpen && "Cadastrar Texto"}
                </NavLink>
                
                <NavLink to="/usuarios" icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                }>
                  {isSidebarOpen && "Gerenciar Usuários"}
                </NavLink>

                <NavLink to="/dashboard" icon={
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }>
                  {isSidebarOpen && "Dashboard"}
                </NavLink>
              </>
            )}
          </nav>

          {/* Theme Toggle and Logout */}
          <div className="mt-auto space-y-4">
            {isSidebarOpen ? (
              <div className="flex items-center justify-between px-2 py-2 bg-white/10 rounded-lg">
                <span className="text-sm text-white/90">Tema</span>
                <ThemeToggle />
              </div>
            ) : (
              <div className="flex justify-center py-2 bg-white/10 rounded-lg">
                <ThemeToggle />
              </div>
            )}
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-white/90 hover:bg-white/20 transition-all duration-200 ease-in-out"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {isSidebarOpen && "Logout"}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-md z-10 transition-colors duration-300">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                {location.pathname === "/" && "Lista de Textos"}
                {location.pathname === "/substituir" && "Substituir Variáveis"}
                {location.pathname === "/cadastrar" && "Cadastrar Texto"}
                {location.pathname.includes("/editar/") && "Editar Texto"}
                {location.pathname === "/usuarios" && "Gerenciar Usuários"}
                {location.pathname === "/dashboard" && "Dashboard - Estatísticas"}
              </h1>
            </div>
            
            {/* Mobile Theme Toggle - Only shown on smaller screens */}
            <div className="md:hidden">
              <ThemeToggle />
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-6 transition-colors duration-300">
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <Routes>
                <Route path="/" element={<TextList userRole={userRole} />} />
                <Route path="/substituir" element={<TextSubstitution />} />
                {userRole === 'admin' && (
                  <>
                    <Route path="/cadastrar" element={<TextForm />} />
                    <Route path="/editar/:id" element={<EditTextForm />} />
                    <Route path="/usuarios" element={<UserManagement />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                  </>
                )}
              </Routes>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default AppContent;